import { useState, useCallback } from 'react';
import Axios from '../config/axios';
import { UrlConstant } from '../commom/UrlConstant';
import { initAeroSyncWidget, AerosyncEnvironment } from 'aerosync-web-sdk';
import type { WidgetEventType } from 'aerosync-web-sdk';
import { getUserFromLocalStorage, setUserInLocalStorage } from '../utils/utils';

export interface AeroPayBankLinkConfig {
    onSuccess?: (connectionId: string) => void;
    onError?: (error: string) => void;
    onClose?: () => void;
    autoRefresh?: boolean;
    successMessage?: string;
    environment?: AerosyncEnvironment;
    onAfterOtpVerified?: () => Promise<boolean>;
}

export interface AeroPayBankLinkState {
    isWidgetOpen: boolean;
    isLinking: boolean;
    isLoading: boolean;
    needsOtpVerification: boolean;
    otp: string;
    isVerifyingOtp: boolean;
    widgetUrl: string;
    error: string | null;
}

/**
 * Waits for a DOM element with the given ID to appear.
 * Uses MutationObserver — no arbitrary timeouts.
 */
const waitForElement = (id: string, timeout = 5000): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (document.getElementById(id)) {
            resolve();
            return;
        }

        const observer = new MutationObserver(() => {
            if (document.getElementById(id)) {
                observer.disconnect();
                resolve();
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        setTimeout(() => {
            observer.disconnect();
            reject(new Error(`Element #${id} not found within ${timeout}ms`));
        }, timeout);
    });
};

export const useAeroPayBankLink = (config: AeroPayBankLinkConfig = {}) => {
    const {
        onSuccess,
        onError,
        onClose,
        autoRefresh = true,
        successMessage = 'Bank account linked successfully!',
        environment =
        process.env.REACT_APP_ENVIRONMENT === 'production'
            ? AerosyncEnvironment.Production
            : AerosyncEnvironment.Sandbox,
        onAfterOtpVerified,
    } = config;

    const [state, setState] = useState<AeroPayBankLinkState>({
        isWidgetOpen: false,
        isLinking: false,
        isLoading: false,
        needsOtpVerification: false,
        otp: '',
        isVerifyingOtp: false,
        widgetUrl: '',
        error: null,
    });

    const [isAeroPayUserState, setIsAeroPayUserState] = useState<boolean>(
        () => getUserFromLocalStorage()?.isAeropayUser === true
    );

    const updateState = useCallback((updates: Partial<AeroPayBankLinkState>) => {
        setState((prev) => ({ ...prev, ...updates }));
    }, []);

    const setOtp = useCallback(
        (otp: string) => {
            updateState({ otp });
        },
        [updateState]
    );

    const checkIsAeroPayUser = useCallback((): boolean => {
        return getUserFromLocalStorage()?.isAeropayUser === true;
    }, []);

    const createAeroPayUser = useCallback(async (): Promise<void> => {
        try {
            await Axios.post(UrlConstant.AeroPay.Createuser);
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || 'Failed to create AeroPay account';
            throw new Error(errorMsg);
        }
    }, []);

    const verifyOtp = useCallback(async (otpCode: string): Promise<void> => {
        try {
            const response = await Axios.post(UrlConstant.AeroPay.VerifyOtp, { code: otpCode });
            const resData = response.data;

            if (resData.status !== 'success') {
                throw new Error(resData.message || 'OTP verification failed');
            }

            const currentUser = getUserFromLocalStorage();
            setUserInLocalStorage({ ...currentUser, isAeropayUser: true });
            setIsAeroPayUserState(true);

            window.dispatchEvent(new Event('user-updated'));
        } catch (error: any) {
            const errorMsg =
                error?.response?.data?.message || error?.message || 'OTP verification failed';
            throw new Error(errorMsg);
        }
    }, []);

    const getLinkBankCredentials = useCallback(async (): Promise<{
        fastlinkURL: string;
        token: string;
        aeropayUserId: string;
    }> => {
        try {
            const response = await Axios.post(UrlConstant.AeroPay.LinkBank);
            const { fastlinkURL, token, aeropayUserId } = response.data.data;

            if (!fastlinkURL || !token || !aeropayUserId) {
                throw new Error('Invalid response from LinkBank API');
            }

            return { fastlinkURL, token, aeropayUserId };
        } catch (error: any) {
            const errorMsg =
                error?.response?.data?.message || 'Failed to get link bank credentials';
            throw new Error(errorMsg);
        }
    }, []);

    const saveBankConnection = useCallback(async (connectionId: string): Promise<void> => {
        try {
            await Axios.post(UrlConstant.AeroPay.Linkbankaccountaggregator, { connectionId });
        } catch (error: any) {
            const errorMsg =
                error?.response?.data?.message || 'Failed to save bank connection';
            throw new Error(errorMsg);
        }
    }, []);

    const initializeWidget = useCallback(
        async (token: string, fastlinkURL: string, aeropayUserId: string) => {
            // 1. Open the modal first so the container div is rendered by React
            updateState({ isWidgetOpen: true, isLoading: false });

            try {
                // 2. Wait for the container to actually exist in the DOM
                await waitForElement('aerosync-widget-container');
            } catch (err) {
                const errorMsg = 'Widget container failed to render. Please try again.';
                updateState({ isWidgetOpen: false, isLoading: false, error: errorMsg });
                onError?.(errorMsg);
                return;
            }

            // 3. Now it's safe to call the SDK — it will find the element
            const widgetRef = initAeroSyncWidget({
                elementId: 'aerosync-widget-container',
                iframeTitle: 'Connect Bank',
                environment,
                token,
                aeroPassUserUuid: aeropayUserId,
                configurationId: process.env.REACT_APP_AEROPAY_CONFIGURATION_ID,
                widgetLaunchType: 'host',
                style: {
                    width: '375px',
                    height: '688px',
                    bgColor: '#141414',
                    opacity: 1,
                },

                onLoad() {
                    updateState({ isLoading: false });
                    console.log('AeroSync widget loaded');
                },

                onSuccess: async (event: any) => {
                    try {
                        const connectionId = event?.connectionId || event?.userId || event?.user_id;
                        if (!connectionId) throw new Error('Connection ID not found');

                        updateState({ isLinking: true });
                        await saveBankConnection(connectionId);

                        // No setTimeout needed — portal isolates SDK DOM from React
                        updateState({ isWidgetOpen: false, isLinking: false, error: null });
                        onSuccess?.(connectionId);
                    } catch (error: any) {
                        const errorMsg = error?.message || 'Bank linked but failed to save connection';
                        updateState({ isWidgetOpen: false, isLinking: false, error: errorMsg });
                        onError?.(errorMsg);
                    }
                },

                onClose() {
                    updateState({ isWidgetOpen: false, isLoading: false, isLinking: false });
                    onClose?.();
                },

                onError(event: any) {
                    const errorMsg = event?.message || 'Failed to link bank account';
                    updateState({ isWidgetOpen: false, isLoading: false, isLinking: false, error: errorMsg });
                    onError?.(errorMsg);
                },

                onEvent(event: WidgetEventType) {
                    if (event?.type === 'close') {
                        updateState({ isWidgetOpen: false, isLoading: false, isLinking: false });
                        onClose?.();
                    }
                },
            });

            // Required per AeroSync docs — must call .launch() after initAeroSyncWidget()
            widgetRef.launch();
        },
        [environment, onSuccess, onError, onClose, updateState, saveBankConnection]
    );

    const startBankLink = useCallback(async (): Promise<void> => {
        try {
            updateState({ isLoading: true, error: null });

            if (!checkIsAeroPayUser()) {
                await createAeroPayUser();
                updateState({
                    isLoading: false,
                    needsOtpVerification: true,
                });
                return;
            }

            const { fastlinkURL, token, aeropayUserId } = await getLinkBankCredentials();
            await initializeWidget(token, fastlinkURL, aeropayUserId);
        } catch (error: any) {
            const errorMsg = error?.message || 'Failed to initiate bank linking';
            updateState({ isLoading: false, error: errorMsg });
            onError?.(errorMsg);
        }
    }, [checkIsAeroPayUser, createAeroPayUser, getLinkBankCredentials, initializeWidget, updateState, onError]);

    const handleOtpVerification = useCallback(async (): Promise<void> => {
        try {
            updateState({ isVerifyingOtp: true, error: null });

            await verifyOtp(state.otp);

            updateState({
                isVerifyingOtp: false,
                needsOtpVerification: false,
                otp: '',
                isLoading: true,
            });

            if (onAfterOtpVerified) {
                const hasAccounts = await onAfterOtpVerified();
                if (hasAccounts) {
                    updateState({ isLoading: false });
                    return;
                }
            }

            const { fastlinkURL, token, aeropayUserId } = await getLinkBankCredentials();

            updateState({
                isVerifyingOtp: false,
                needsOtpVerification: false,
                otp: '',
                isLoading: false,
            });

            await initializeWidget(token, fastlinkURL, aeropayUserId);
        } catch (error: any) {
            const errorMsg = error?.message || 'OTP verification failed';
            updateState({
                isVerifyingOtp: false,
                error: errorMsg,
                isLoading: false,
            });
            onError?.(errorMsg);
        }
    }, [state.otp, verifyOtp, getLinkBankCredentials, initializeWidget, updateState, onError, onAfterOtpVerified]);

    const cancelOtpVerification = useCallback(() => {
        updateState({
            needsOtpVerification: false,
            otp: '',
            error: null,
            isLoading: false,
        });
    }, [updateState]);

    const closeWidget = useCallback(() => {
        updateState({
            isWidgetOpen: false,
            isLinking: false,
            isLoading: false,
        });
        onClose?.();
    }, [updateState, onClose]);

    const reset = useCallback(() => {
        setState({
            isWidgetOpen: false,
            isLinking: false,
            isLoading: false,
            needsOtpVerification: false,
            otp: '',
            isVerifyingOtp: false,
            widgetUrl: '',
            error: null,
        });
    }, []);

    return {
        ...state,
        startBankLink,
        handleOtpVerification,
        cancelOtpVerification,
        closeWidget,
        setOtp,
        reset,
        isAeroPayUser: isAeroPayUserState,
    } as const;
};

export default useAeroPayBankLink;